import type { Page } from '@hello-aria/api-client'
import { toListQuery } from '@hello-aria/makalu-api-client'
import type { QueryStringInput } from '@hello-aria/query-string'
import { isNil } from 'lodash'

import type { Proof, ProofTarget, ValidProofDraft } from '@/shared/models/proofModels'
import { toProofCreationBody } from '@/shared/models/proofModels'
import { requireFileId } from '@/shared/models/uploadModels'
import { downloadFiles } from '@/shared/services/downloadServices'
import { downloadAttachments, uploadFiles } from '@/shared/services/uploadServices'
import { makaluApi } from '@/shared/utils/makaluApiClient'
import { fetchAllPages } from '@/shared/utils/requestsUtils'

type ListProofs = {
    companyId: string
    query?: QueryStringInput
}

export const listProofs = ({ companyId, query = {} }: ListProofs): Promise<Page<Proof>> =>
    makaluApi.listPage('/companies/{companyId}/proofs', {
        params: { path: { companyId }, query: toListQuery(query) }
    })

/** Every proof of a query: the callers show the whole document list of one target (DATA-7). */
export const listAllProofs = ({
    companyId,
    query = {},
    signal
}: ListProofs & { signal?: AbortSignal }): Promise<Proof[]> =>
    fetchAllPages((pageQuery) => listProofs({ companyId, query: pageQuery }), query, signal)

const uploadProofFile = async (companyId: string, file: File): Promise<string> => {
    // uploadFiles logs a failed upload and returns no upload, so its absence is the failure to surface.
    const [upload] = (await uploadFiles([file], companyId)) ?? []

    if (isNil(upload)) throw new Error(`The file ${file.name} could not be uploaded.`)

    return requireFileId(upload)
}

type CreateProofs = {
    companyId: string
    drafts: ValidProofDraft[]
    targets?: ProofTarget[]
}

/** Uploads the file of each draft, then creates its proof on the targets given. Resolves the ids of the proofs. */
export const createProofs = async ({ companyId, drafts, targets }: CreateProofs): Promise<Proof['id'][]> => {
    const proofIds: Proof['id'][] = []

    for (const draft of drafts) {
        const fileId = await uploadProofFile(companyId, draft.file)
        const proof = await makaluApi.POST('/companies/{companyId}/proofs', {
            params: { path: { companyId } },
            body: toProofCreationBody(fileId, draft, targets)
        })
        proofIds.push(proof.id)
    }

    return proofIds
}

type UnlinkProof = {
    companyId: string
    proofId: Proof['id']
    target: ProofTarget
}

/** Detaches the proof from this one target only: the file stays a proof of its other targets. */
export const unlinkProof = async ({ companyId, proofId, target }: UnlinkProof): Promise<void> => {
    await makaluApi.POST('/companies/{companyId}/proofs/{proofId}/unlink', {
        params: { path: { companyId, proofId } },
        body: { targets: [target] }
    })
}

type GetProofFile = {
    companyId: string
    proof: Proof
}

export const getProofFile = async ({ companyId, proof }: GetProofFile): Promise<File> => {
    const { id, fileId, legacyUrl } = proof

    if (!isNil(fileId)) {
        const [file] = await downloadFiles(companyId, [fileId])

        if (isNil(file)) throw new Error(`The file ${fileId} of the proof ${id} could not be downloaded.`)

        return file
    }

    if (!isNil(legacyUrl)) {
        const [attachment] = (await downloadAttachments([{ url: legacyUrl }])) ?? []

        if (isNil(attachment)) throw new Error(`The legacy document of the proof ${id} could not be downloaded.`)

        return attachment.file
    }

    throw new Error(`The proof ${id} carries neither a file nor a legacy URL.`)
}
